import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ImageRejected, prepareCouplePhoto } from '@/lib/image-downscale';

/**
 * Saving and clearing one wedding's couple photo.
 *
 * Two surfaces upload this picture — the edit dialog's labelled field and the
 * hub header's portrait — and they differ only in what surrounds the control.
 * The shared thing is therefore the behaviour, not a component: what a planner
 * is told when an upload lands, which queries have to be refetched because they
 * carry the photo, and how a file the browser will not resize is reported.
 *
 * The resize happens here, in the browser, for the reason
 * `lib/image-downscale.ts` sets out at length: the planner's 4MB phone original
 * never crosses the network, and the server's ceiling stays a backstop instead
 * of a routine rejection.
 */
export function useCouplePhoto(weddingId: string) {
  const queryClient = useQueryClient();

  // Bumped on every successful change, for callers that render `CouplePhoto`
  // themselves against a wedding they are not refetching. The photo URL is the
  // wedding id, so a replacement reuses it and the browser would otherwise show
  // the cached previous picture — this is what makes the new one appear. A
  // caller reading a fresh `photoKey` off an invalidated query (the hub header)
  // already has a value that changes per upload and can pass that instead.
  const [version, setVersion] = useState(0);

  const invalidate = () => {
    setVersion((v) => v + 1);
    queryClient.invalidateQueries({ queryKey: ['wedding', weddingId] });
    queryClient.invalidateQueries({ queryKey: ['weddings'] });
  };

  const upload = useMutation({
    mutationFn: async (file: File) => {
      let prepared;
      try {
        prepared = await prepareCouplePhoto(file);
      } catch (err) {
        // `ImageRejected` is already a sentence written for the planner — "that
        // file is not a PNG, JPEG, GIF, or WebP image". Anything else out of the
        // canvas is not, so it does not get shown to them verbatim.
        throw err instanceof ImageRejected
          ? err
          : new Error('That photo could not be read. Try another.');
      }

      const body = new FormData();
      body.append('photo', prepared.file);

      // Plain `fetch` rather than the Hono RPC client: the generated client
      // serializes JSON, and this endpoint takes multipart.
      const res = await fetch(`/api/weddings/${weddingId}/photo`, {
        method: 'POST',
        body,
        credentials: 'include',
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(err.error ?? 'That photo could not be saved.');
      }
      return res.json();
    },
    onSuccess: () => {
      invalidate();
      toast.success('Photo saved');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const remove = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/weddings/${weddingId}/photo`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(err.error ?? 'That photo could not be removed.');
      }
      return res.json();
    },
    onSuccess: () => {
      invalidate();
      toast.success('Photo removed');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return {
    version,
    /** Either request is in flight: one control, so one disabled state. */
    busy: upload.isPending || remove.isPending,
    uploading: upload.isPending,
    /** Takes the input's `files[0]` directly, undefined and all. */
    pick: (file: File | undefined) => {
      if (file) upload.mutate(file);
    },
    remove: () => remove.mutate(),
  };
}
